<template>
  <div class="enrichment-table-container">
    <!-- Data Table -->
    <div class="table-wrapper">
      <el-table
        :data="filteredData"
        stripe
        border
        :height="tableHeight"
        style="width: 100%"
        :default-sort="{ prop: 'fdr', order: 'ascending' }"
        @sort-change="handleSortChange"
      >
        <el-table-column
          prop="source"
          label="Source"
          width="200"
          sortable="custom"
        >
          <template #default="{ row }">
            <el-tag :type="getSourceTagType(row.source)" size="small">
              {{ row.source }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column
          prop="term"
          label="Pathway/Term"
          min-width="300"
          sortable="custom"
          show-overflow-tooltip
        >
        </el-table-column>

        <el-table-column
          prop="overlap"
          label="Overlap"
          width="100"
          sortable="custom"
          align="center"
        >
          <template #default="{ row }">
            <span class="numeric-cell">{{ row.overlap }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="n_term_genes"
          label="Term Size"
          width="110"
          sortable="custom"
          align="center"
        >
          <template #default="{ row }">
            <span class="numeric-cell">{{ row.n_term_genes }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="gene_ratio"
          label="Protein Ratio"
          width="120"
          sortable="custom"
          align="center"
        >
          <template #default="{ row }">
            <span class="numeric-cell">{{ formatRatio(row.gene_ratio) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="odds_ratio"
          label="Odds Ratio"
          width="120"
          sortable="custom"
          align="center"
        >
          <template #default="{ row }">
            <span class="numeric-cell">{{ formatNumber(row.odds_ratio, 2) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="pval"
          label="P-value"
          width="120"
          sortable="custom"
          align="center"
        >
          <template #default="{ row }">
            <span class="numeric-cell">{{ formatScientific(row.pval) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          prop="fdr"
          label="FDR"
          width="120"
          sortable="custom"
          align="center"
        >
          <template #default="{ row }">
            <span class="numeric-cell">{{ formatScientific(row.fdr) }}</span>
          </template>
        </el-table-column>

        <el-table-column
          label="Proteins"
          width="100"
          align="center"
        >
          <template #default="{ row }">
            <el-button size="mini" @click="showGeneDetails(row)">
              View ({{ row.overlap }})
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Pagination & Download -->
    <div class="pagination-bar">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pageSize"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
      >
      </el-pagination>
      <el-button 
        size="small" 
        type="primary" 
        icon="el-icon-download"
        @click="exportToCSV"
        class="download-btn"
      >
        Download CSV
      </el-button>
    </div>

    <!-- Protein Details Dialog -->
    <el-dialog
      title="Overlapping Proteins"
      :visible.sync="geneDialogVisible"
      width="600px"
    >
      <div v-if="selectedRow">
        <div class="dialog-section">
          <h4>{{ selectedRow.term }}</h4>
          <p class="source-label">Source: <el-tag :type="getSourceTagType(selectedRow.source)" size="small">{{ selectedRow.source }}</el-tag></p>
        </div>
        <div class="dialog-section">
          <p><strong>Overlap:</strong> {{ selectedRow.overlap }} / {{ selectedRow.n_term_genes }} proteins</p>
          <p><strong>P-value:</strong> {{ formatScientific(selectedRow.pval) }}</p>
          <p><strong>FDR:</strong> {{ formatScientific(selectedRow.fdr) }}</p>
          <p><strong>Odds Ratio:</strong> {{ formatNumber(selectedRow.odds_ratio, 2) }}</p>
        </div>
        <div class="dialog-section">
          <h5>Proteins:</h5>
          <div class="gene-list">
            <el-tag
              v-for="gene in getGeneList(selectedRow.overlap_genes)"
              :key="gene"
              size="small"
              class="gene-tag"
            >
              {{ gene }}
            </el-tag>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'EnrichmentTable',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    tableHeight: {
      type: [Number, String],
      default: '100%'
    }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 20,
      sortProp: 'fdr',
      sortOrder: 'ascending',
      geneDialogVisible: false,
      selectedRow: null
    }
  },
  computed: {
    // Sort data
    sortedData() {
      if (!this.data || this.data.length === 0) {
        return []
      }
      
      const data = [...this.data]
      
      if (this.sortProp) {
        data.sort((a, b) => {
          let aVal = a[this.sortProp]
          let bVal = b[this.sortProp]
          
          // Handle numeric sorting
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return this.sortOrder === 'ascending' ? aVal - bVal : bVal - aVal
          }
          
          // Handle string sorting
          aVal = String(aVal || '')
          bVal = String(bVal || '')
          
          if (this.sortOrder === 'ascending') {
            return aVal.localeCompare(bVal)
          } else {
            return bVal.localeCompare(aVal)
          }
        })
      }
      
      return data
    },
    
    // Paginate data
    filteredData() {
      const start = (this.currentPage - 1) * this.pageSize
      const end = start + this.pageSize
      return this.sortedData.slice(start, end)
    },
    
    total() {
      return this.sortedData.length
    }
  },
  methods: {
    handleSizeChange(val) {
      this.pageSize = val
      this.currentPage = 1
    },
    
    handleCurrentChange(val) {
      this.currentPage = val
    },
    
    handleSortChange({ prop, order }) {
      this.sortProp = prop
      this.sortOrder = order
      this.currentPage = 1
    },
    
    showGeneDetails(row) {
      this.selectedRow = row
      this.geneDialogVisible = true
    },
    
    getGeneList(genesString) {
      if (!genesString) return []
      return genesString.split(';').filter(g => g.trim())
    },
    
    getSourceTagType(source) {
      const s = (source || '').toLowerCase()
      if (s.includes('go')) return 'primary'
      if (s.includes('kegg')) return 'success'
      if (s.includes('reactome')) return 'warning'
      if (s.includes('wiki')) return 'info'
      if (s.includes('hallmark') || s.includes('msigdb')) return 'danger'
      return ''
    },
    
    formatRatio(value) {
      if (value === null || value === undefined) return 'N/A'
      return Number(value).toFixed(3)
    },
    
    formatNumber(value, decimals = 2) {
      if (value === null || value === undefined) return 'N/A'
      return Number(value).toFixed(decimals)
    },
    
    formatScientific(value) {
      if (value === null || value === undefined) return 'N/A'
      return Number(value).toExponential(2)
    },
    
    exportToCSV() {
      if (!this.sortedData || this.sortedData.length === 0) {
        this.$notify({
          title: 'Warning',
          message: 'No data to export',
          type: 'warning',
          position: 'top-right'
        })
        return
      }
      
      // Define headers
      const headers = [
        'Source',
        'Term',
        'Overlap',
        'Term Size',
        'Protein Ratio',
        'Odds Ratio',
        'P-value',
        'FDR',
        'Overlap Proteins'
      ]
      
      // Build CSV content
      let csvContent = headers.join(',') + '\n'
      
      this.sortedData.forEach(row => {
        const rowData = [
          this.escapeCSV(row.source),
          this.escapeCSV(row.term),
          row.overlap,
          row.n_term_genes,
          this.formatRatio(row.gene_ratio),
          this.formatNumber(row.odds_ratio, 2),
          row.pval,
          row.fdr,
          this.escapeCSV(row.overlap_genes)
        ]
        csvContent += rowData.join(',') + '\n'
      })
      
      // Create and trigger download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `enrichment_results_${new Date().getTime()}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      this.$notify({
        title: 'Success',
        message: 'CSV exported successfully',
        type: 'success',
        position: 'top-right'
      })
    },
    
    escapeCSV(value) {
      if (value === null || value === undefined) return ''
      const str = String(value)
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"'
      }
      return str
    }
  }
}
</script>

<style scoped>
.enrichment-table-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #fff;
}

.table-wrapper {
  flex: 1;
  overflow: auto;
  padding: 8px;
}

.numeric-cell {
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  font-size: 13px;
}

.pagination-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 12px 16px;
  background: #f8f9fa;
  border-top: 1px solid #e0e0e0;
}

.download-btn {
  margin-left: auto;
}

.dialog-section {
  margin-bottom: 20px;
}

.dialog-section h4 {
  margin: 0 0 8px 0;
  font-size: 16px;
  color: #333;
}

.dialog-section h5 {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #555;
}

.dialog-section p {
  margin: 4px 0;
  font-size: 14px;
  color: #666;
}

.source-label {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gene-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
  padding: 12px;
  background: #f9fafb;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.gene-tag {
  cursor: default;
}

/* Table styling enhancements */
::v-deep .el-table th {
  background-color: #f3f4f6 !important;
  color: #374151 !important;
  font-weight: 600;
  font-size: 13px;
}

::v-deep .el-table td {
  font-size: 13px;
}

::v-deep .el-table--striped .el-table__body tr.el-table__row--striped td {
  background: #fafafa;
}

::v-deep .el-table__body tr:hover > td {
  background-color: #f0f7ff !important;
}
</style>

